import { useQueries } from "@tanstack/react-query";
import apiClient from "@/app/api/client";

interface EmployeeNameResponse {
	success: true;
	data: { id: string; fullName: string };
}

/** Ism kamdan-kam o'zgaradi — qayta so'rov jadval har varaqlanganda kerak emas. */
const NAME_STALE_TIME_MS = 5 * 60_000;

export interface EmployeeOptionItem {
	value: string;
	label: string;
}

/** Bo'sh qiymatlarni tashlaydi va takrorlarni yig'adi — so'rovlar soni shu ro'yxatga teng. */
export function uniqueEmployeeIds(ids: Array<string | null | undefined>): string[] {
	const unique = new Set<string>();

	for (const id of ids) {
		if (id) {
			unique.add(id);
		}
	}

	// Tartiblash — `useQueries` massivining tartibi barqaror bo'lsin (kesh kaliti
	// har qatorga alohida, lekin barqaror tartib ortiqcha renderni kamaytiradi).
	return [...unique].sort();
}

/**
 * Qidiruv natijalariga TANLANGAN qiymatni qo'shadi.
 *
 * ⚠️ Antd `Select` yorliqni faqat `options` ichidan topadi. Tanlangan xodim
 * joriy qidiruv sahifasida bo'lmasa (yozish boshlangan yoki u birinchi 100
 * natijadan tashqarida), yorliq o'rnida xom UUID chiqardi.
 */
export function withSelectedEmployeeOption(
	options: EmployeeOptionItem[],
	selectedId: string | null | undefined,
	names: Map<string, string>
): EmployeeOptionItem[] {
	if (!selectedId || options.some((option) => option.value === selectedId)) {
		return options;
	}

	return [{ value: selectedId, label: names.get(selectedId) ?? selectedId }, ...options];
}

/**
 * Xodim ID → F.I.Sh.
 *
 * ⚠️ Nomni QIDIRUV natijalaridan olish mumkin emas: `GET /employees` bitta
 * sahifani (`limit: 100`) va `q` bilan filtrlangan ro'yxatni qaytaradi, ya'ni
 * qidiruvga bitta harf yozilishi bilan allaqachon ko'rsatilgan qatorlar ismdan
 * xom UUID ga aylanardi, birinchi 100 tadan tashqaridagi xodimlar esa umuman
 * ism olmasdi. Shuning uchun KERAKLI ID lar `GET /employees/{id}` orqali
 * alohida olinadi va TanStack Query keshida qoladi.
 *
 * ⚠️ Qaytgan `Map` ning HAVOLASI har renderda o'zgarishi mumkin — uni
 * `useEffect` / `useMemo` bog'liqligiga QO'YMANG (`SettingsPage` dagi cheksiz
 * sikl regressiyasi aynan shu naqshdan chiqqan edi).
 */
export function useEmployeeNames(ids: Array<string | null | undefined>): Map<string, string> {
	const wanted = uniqueEmployeeIds(ids);

	return useQueries({
		queries: wanted.map((id) => ({
			queryKey: ["employees", "name", id],
			queryFn: async () => {
				const response = await apiClient.get<EmployeeNameResponse>(`/employees/${id}`);

				return { id, fullName: response.data.data.fullName };
			},
			staleTime: NAME_STALE_TIME_MS,
			// Ruxsat yo'q yoki xodim ko'lamdan tashqarida bo'lsa 403/404 keladi —
			// qayta urinish faqat kechikish qo'shardi, natija baribir ID bo'lib qoladi.
			retry: false,
		})),
		combine: (results) => {
			const names = new Map<string, string>();

			for (const result of results) {
				if (result.data) {
					names.set(result.data.id, result.data.fullName);
				}
			}

			return names;
		},
	});
}
